import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Combine, Split, Users, X, RefreshCw } from "lucide-react";

const STATUSES = ["all", "pending", "assigned", "driver_en_route", "arrived", "in_progress", "completed", "cancelled"];
const COLOR = {
  pending: "bg-amber-50 text-amber-700",
  assigned: "bg-blue-50 text-blue-700",
  driver_en_route: "bg-primary-50 text-primary-700",
  arrived: "bg-emerald-50 text-emerald-700",
  in_progress: "bg-primary-50 text-primary-700",
  completed: "bg-slate-100 text-slate-700",
  cancelled: "bg-rose-50 text-rose-700",
};

const MERGEABLE_STATUSES = new Set(["pending", "assigned", "driver_en_route", "arrived"]);
const MERGEABLE_TYPES = new Set(["shared", "ride_now"]);

function groupByTripId(items) {
  const acc = {};
  for (let i = 0; i < items.length; i++) {
    const bk = items[i];
    if (!bk.trip_id) continue;
    if (!acc[bk.trip_id]) acc[bk.trip_id] = [];
    acc[bk.trip_id].push(bk);
  }
  return acc;
}

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [mergeDriverId, setMergeDriverId] = useState("");

  async function load(s = status) {
    setLoading(true);
    try {
      const url = s === "all" ? "/admin/bookings" : `/admin/bookings?status=${s}`;
      const { data } = await api.get(url);
      setItems(data || []);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = status === "all" ? "/admin/bookings" : `/admin/bookings?status=${status}`;
        const { data } = await api.get(url);
        if (!cancelled) setItems(data || []);
      } catch (e) {
        if (!cancelled) toast.error(fmtErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  // Load online drivers once so the dispatcher can pick one when merging.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/drivers/online");
        if (cancelled) return;
        setDrivers(data || []);
        if ((data || []).length && !mergeDriverId) setMergeDriverId(data[0].id);
      } catch (e) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group bookings by trip_id (excluding solo trips). Pure derivation each render.
  const tripRoster = groupByTripId(items);
  const selectedArray = [...selected];
  const canMerge = selected.size >= 2;
  const selectedBookings = items.filter((b) => selected.has(b.id));
  // All selected must be mergeable (shared/ride_now and not closed)
  const selectionValid = canMerge && selectedBookings.every(
    (b) => MERGEABLE_TYPES.has(b.type) && MERGEABLE_STATUSES.has(b.status)
  );

  function toggle(id) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function doMerge() {
    if (!selectionValid) {
      toast.error("Only shared/ride_now bookings in active states can be merged.");
      return;
    }
    setBusy(true);
    try {
      const payload = { booking_ids: selectedArray };
      // Include a driver_id when the user picks one (or default-selected by first online driver),
      // so the backend doesn't need to discover a nearby idle driver.
      if (mergeDriverId) payload.driver_id = mergeDriverId;
      const { data } = await api.post("/admin/bookings/merge", payload);
      toast.success(`Merged ${data.passenger_count} bookings into trip`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function doSplit(bookingId) {
    setBusy(true);
    try {
      await api.post("/admin/bookings/split", { booking_id: bookingId });
      toast.success("Booking detached from trip — back to pending");
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Operations</p>
          <h1 className="font-display font-bold text-3xl text-slate-900">All Bookings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tick two or more shared bookings to <strong>merge</strong> them into one trip, or split a grouped
            booking back to pending. Auto-grouping handles the obvious cases; this is your manual override for surges.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="bookings-refresh"
          className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-slate-50 transition"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`booking-filter-${s}`}
            onClick={() => { setSelected(new Set()); setStatus(s); }}
            className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${status === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {/* Selection toolbar */}
      {selected.size > 0 && (
        <div
          data-testid="bookings-merge-bar"
          className="sticky top-2 z-30 mb-3 bg-slate-900 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs uppercase tracking-widest text-primary font-bold">{selected.size} selected</span>
            <span className="text-xs text-slate-300 truncate">
              {selectedBookings.map((b) => b.ref).join(", ")}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {drivers.length > 0 ? (
              <select
                data-testid="bookings-merge-driver"
                value={mergeDriverId}
                onChange={(e) => setMergeDriverId(e.target.value)}
                className="h-9 px-2 rounded-lg bg-slate-800 text-white text-xs font-semibold border border-slate-700 focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none max-w-[160px]"
                title="Driver to dispatch the merged trip"
              >
                <option value="">Auto-assign nearest driver</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.vehicle?.plate_number ? ` · ${d.vehicle.plate_number}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-[10px] text-amber-300 uppercase tracking-wider font-bold">No online drivers</span>
            )}
            <button
              onClick={() => setSelected(new Set())}
              className="h-9 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold inline-flex items-center gap-1.5 transition"
              data-testid="bookings-merge-cancel"
            >
              <X size={14} /> Clear
            </button>
            <button
              onClick={doMerge}
              disabled={!selectionValid || busy}
              data-testid="bookings-merge-confirm"
              className="h-9 px-4 rounded-lg bg-primary hover:bg-primary-600 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title={selectionValid ? "" : "All selected bookings must be shared/ride_now and active"}
            >
              <Combine size={14} /> Merge into one trip
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-10 px-3 py-3 border-b border-slate-200"></th>
              {["Ref", "Employee", "Type", "Pickup → Drop-off", "Status", "Driver / Group", "Created", ""].map((h) => (
                <th key={h} className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold px-5 py-3 border-b border-slate-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const roster = b.trip_id ? (tripRoster[b.trip_id] || []) : [];
              const isGrouped = roster.length >= 2;
              const canSelect = MERGEABLE_TYPES.has(b.type) && MERGEABLE_STATUSES.has(b.status);
              const checked = selected.has(b.id);
              return (
                <tr
                  key={b.id}
                  data-testid={`booking-row-${b.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${checked ? "bg-primary-50/40" : ""}`}
                >
                  <td className="px-3 py-3 align-top">
                    <input
                      type="checkbox"
                      data-testid={`booking-select-${b.id}`}
                      checked={checked}
                      disabled={!canSelect}
                      onChange={() => toggle(b.id)}
                      className="w-4 h-4 accent-primary disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
                      title={canSelect ? "" : "Only active shared / ride_now bookings can be merged"}
                    />
                  </td>
                  <td className="px-5 py-3 text-xs font-mono font-semibold text-slate-900">{b.ref}</td>
                  <td className="px-5 py-3 text-sm text-slate-700">{b.user?.name}</td>
                  <td className="px-5 py-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{b.type}</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600 max-w-xs truncate">
                    {b.pickup_address?.split(",")[0]} → {b.dropoff_address?.split(",")[0]}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${COLOR[b.status] || "bg-slate-100 text-slate-700"}`}>
                      {b.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-700">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate max-w-[140px]">{b.driver?.name || "—"}</span>
                      {isGrouped && (
                        <span
                          data-testid={`booking-group-badge-${b.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary-700 font-bold uppercase tracking-wider"
                          title={`Trip has ${roster.length} bookings: ${roster.map((r) => r.ref).join(", ")}`}
                        >
                          <Users size={10} /> +{roster.length - 1}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">
                    {new Date(b.created_at).toLocaleString("en-AE", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {isGrouped && b.status !== "in_progress" && b.status !== "completed" && (
                      <button
                        onClick={() => doSplit(b.id)}
                        disabled={busy}
                        data-testid={`booking-split-${b.id}`}
                        className="h-8 px-2 rounded-lg border border-slate-200 hover:border-rose-300 hover:text-rose-700 text-slate-500 text-[11px] font-semibold inline-flex items-center gap-1 transition disabled:opacity-50"
                        title="Detach from this shared trip"
                      >
                        <Split size={12} /> Split
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">No bookings found</div>
        )}
      </div>
    </div>
  );
}
